import React from 'react';
import { motion } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { ArrowRight, CheckCircle2, Mail } from 'lucide-react';
type JoinTeamFormProps = {
  variant?: 'default' | 'compact';
};
const helpAreas = ['Engineering · Full-stack', 'Engineering · Security', 'Engineering · AI systems', 'Security research', 'Product design', 'Go-to-market / sales', 'Marketing / brand', 'MSSP / partnerships', 'Community / DevRel', 'Advisor / angel', 'Something else'];
export function JoinTeamForm({
  variant = 'default'
}: JoinTeamFormProps) {
  const [state, handleSubmit] = useForm('mykvgpkn');
  const {
    submitting,
    succeeded
  } = state;
  if (succeeded) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="rounded-2xl bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border border-[#1a8fd4]/30 p-10 text-center" data-id="element-972">
        <CheckCircle2 className="w-14 h-14 text-[#1a8fd4] mx-auto mb-5" data-id="element-973" />
        <h3 className="text-2xl font-semibold text-white mb-2" data-id="element-974">
          Got it. We'll be in touch.
        </h3>
        <p className="text-base text-white/60 max-w-md mx-auto" data-id="element-975">
          Sean reads every one of these. If there's a fit, you'll hear back
          directly.
        </p>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="rounded-2xl bg-[#070a0e] border border-white/10 p-6 md:p-8 space-y-5" data-id="element-976">
      {/* Hidden field so Formspree replies route correctly */}
      <input type="hidden" name="form_source" value="join-the-team" data-id="element-977" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-978">
        <div data-id="element-979">
          <label htmlFor="jt-name" className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2" data-id="element-980">
            Name
          </label>
          <input id="jt-name" name="name" type="text" required placeholder="Your name" className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-[#1a8fd4]/60 focus:outline-none focus:ring-1 focus:ring-[#1a8fd4]/40 transition-colors" data-id="element-981" />
          <ValidationError prefix="Name" field="name" errors={state.errors} className="mt-1 text-xs text-red-400" data-id="element-982" />
        </div>
        <div data-id="element-983">
          <label htmlFor="jt-email" className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2" data-id="element-984">
            Email
          </label>
          <input id="jt-email" name="email" type="email" required placeholder="you@company.com" className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-[#1a8fd4]/60 focus:outline-none focus:ring-1 focus:ring-[#1a8fd4]/40 transition-colors" data-id="element-985" />
          <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-1 text-xs text-red-400" data-id="element-986" />
        </div>
      </div>

      <div data-id="element-987">
        <label htmlFor="jt-help" className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2" data-id="element-988">
          How do you want to help?
        </label>
        <select id="jt-help" name="help_area" required defaultValue="" className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white focus:border-[#1a8fd4]/60 focus:outline-none focus:ring-1 focus:ring-[#1a8fd4]/40 transition-colors" data-id="element-989">
          <option value="" disabled className="bg-[#070a0e]" data-id="element-990">
            Pick the closest area…
          </option>
          {helpAreas.map(h => <option key={h} value={h} className="bg-[#070a0e]" data-id="element-991">
              {h}
            </option>)}
        </select>
      </div>

      <div data-id="element-992">
        <label htmlFor="jt-links" className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2" data-id="element-993">
          LinkedIn / GitHub / portfolio{' '}
          <span className="text-white/30 normal-case" data-id="element-994">(optional)</span>
        </label>
        <input id="jt-links" name="links" type="text" placeholder="linkedin.com/in/… or github.com/…" className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-[#1a8fd4]/60 focus:outline-none focus:ring-1 focus:ring-[#1a8fd4]/40 transition-colors" data-id="element-995" />
      </div>

      <div data-id="element-996">
        <label htmlFor="jt-message" className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2" data-id="element-997">
          What do you want to build with us?
        </label>
        <textarea id="jt-message" name="message" rows={variant === 'compact' ? 3 : 5} required placeholder="A few sentences on what excites you and how you'd contribute." className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-[#1a8fd4]/60 focus:outline-none focus:ring-1 focus:ring-[#1a8fd4]/40 transition-colors resize-none" data-id="element-998" />
        <ValidationError prefix="Message" field="message" errors={state.errors} className="mt-1 text-xs text-red-400" data-id="element-999" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2" data-id="element-1000">
        <p className="text-xs text-white/40 flex items-center gap-2" data-id="element-1001">
          <Mail className="w-3 h-3" data-id="element-1002" />
          Goes directly to the founder. No recruiter funnel.
        </p>
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1003">
          {submitting ? 'Sending…' : 'Send'}
          <ArrowRight className="w-3.5 h-3.5" data-id="element-1004" />
        </button>
      </div>
    </form>;
}